import { useEffect, useReducer } from "react";

import type { ViewModel } from "@lib/viewModel";

import { useKoObservableOrRawValue } from "@services/useKoObservableOrRawValue";
import { useViewModel } from "@services/useViewModel";

/**
 * returns a useState-like tuple of [value, setValue] for any member of the meta view model
 *
 * @example
 * const [appGroupId, setAppGroupId] = useMetaViewModel("appGroupId");
 */
export function useMetaViewModel<Key extends keyof ViewModel["meta"]>(field: Key) {
  const [meta] = useViewModel("meta");
  const subObservable = meta?.[field];
  const [, forceUpdate] = useReducer(() => ({}), {});

  useEffect(() => {
    if (!meta?.[field]) {
      // because we have no way of knowing when the child observable will exist for sub-fields of the view model,
      // we need to check continuously and force an update when it does exist
      const checkChild = () => {
        if (meta?.[field]) {
          forceUpdate();
        } else {
          requestAnimationFrame(checkChild);
        }
      };
      requestAnimationFrame(checkChild);
    }
  }, [field, meta]);

  return useKoObservableOrRawValue(subObservable);
}
